import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FieldErrors, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import SurveyForm from "../components/form/SurveyForm";
import QuestionCard from "../components/form/QuestionCard";
import { SurveyActionButtons } from "../components/SurveyActionButtons";
import { useCreateSurveySubmission, useSurveyQuestionEditor } from "../hooks";
import { surveyFormSchema } from "../schemas/survey.schema";
import type { SurveyFormValues } from "../types/survey.types";
import { getFirstSurveyFormErrorMessage } from "../utils/surveyFormValidation";
import { getSurveyQuestionErrorMessages } from "../utils/surveyQuestionErrorMessages";
import FormHeader from "../../../../components/form/FormHeader";
import { useToast } from "../../../../components/toast-container";
import { goBackOrGoHome } from "../../../../utils/navigation";

function CreateSurveyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [previewMode, setPreviewMode] = useState(false);
  const surveyForm = useForm<SurveyFormValues, undefined, SurveyFormValues>({
    resolver: zodResolver(surveyFormSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      category: "customer_feedback",
      points: 100,
      endTime: undefined,
      questions: [],
    },
  });
  const { handleSaveDraft, isLoading, loadingState } = useCreateSurveySubmission();
  const { questions, lastAddedQuestionId, actions } = useSurveyQuestionEditor(surveyForm);
  const questionErrorMessages = getSurveyQuestionErrorMessages(
    questions,
    surveyForm.formState.errors,
  );

  const handleSaveDraftWithValidation = surveyForm.handleSubmit(
    async (formValues) => {
      await handleSaveDraft(formValues);
    },
    (errors: FieldErrors<SurveyFormValues>) => {
      addToast({
        type: "error",
        title: t("common.error"),
        message: getFirstSurveyFormErrorMessage(errors) ?? t("surveys.messages.save_failed"),
      });
    },
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <FormHeader
        onBack={() => navigate("/merchant/surveys")}
        actions={
          <SurveyActionButtons
            onPreview={() => setPreviewMode((currentMode) => !currentMode)}
            onSaveDraft={handleSaveDraftWithValidation}
            isLoading={isLoading}
            loadingState={loadingState}
            isPreviewMode={previewMode}
          />
        }
      />

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <SurveyForm form={surveyForm} />
        <QuestionCard
          questions={questions}
          isPreviewMode={previewMode}
          actions={actions}

          validation={{
            message: questionErrorMessages.questionsError,
            questionMessages: questionErrorMessages.questionErrorMessages,
            optionMessages: questionErrorMessages.optionErrorMessages,
            scaleLabelMessages: questionErrorMessages.scaleLabelErrorMessages,
            dateRangeMessages: questionErrorMessages.dateRangeErrorMessages,
            yesNoLabelMessages: questionErrorMessages.yesNoLabelErrorMessages,
          }}
          lastAddedQuestionId={lastAddedQuestionId}
        />
      </div>
    </div>
  );
}

export default CreateSurveyPage;
